import { useEffect, useRef, useState } from "react";

import { shell } from "./host.js";

/** What the stack needs of the browser's history; the real one, or a fake in tests. */
export interface HistoryLike {
  pushState(state: unknown, title: string): void;
  back(): void;
}

/** Closes the thing that was opened; called when the browser goes back to before it. */
export type Closer = () => void;
/** Forgets the entry because the thing was closed by hand; drops its history entry if it is the newest. */
export type Release = () => void;

export interface BackStack {
  /** A thing has opened: give it a history entry, so Back closes it. */
  push(close: Closer): Release;
  /** The browser went back one entry. */
  onPop(): void;
  /**
   * Put the guard entry under the app, once. Called on the first thing the
   * user does rather than at start: Chrome skips history entries a page adds
   * before the user has touched it, so an entry pushed at start would not
   * stop the first Back.
   */
  armGuard(): void;
  depth(): number;
}

export interface BackStackOptions {
  /**
   * Keep one entry under the app, so that Back with nothing open warns
   * rather than leaves, and a second press within `leaveWithin` leaves; as
   * Android apps do. For the installed app; a browser tab keeps its Back.
   */
  readonly guard?: boolean;
  readonly onLeaveWarning?: () => void;
  readonly leaveWithin?: number;
  readonly now?: () => number;
}

/** How long the second press has to come after the warning. */
export const LEAVE_WITHIN_MS = 2000;

/**
 * The Back button, as an installed app on a phone expects it: every pane,
 * page and popup that opens pushes one history entry, and going back closes
 * the newest of them. Once nothing is open the page's history is at its
 * start, so Back leaves the app, the way it leaves WhatsApp at the chat
 * list; with the guard on, the first such press warns and the second leaves.
 * Closing something by hand takes its entry back out, so the button never
 * has to be pressed twice for nothing.
 *
 * With no history at all (`null`), the stack is the same list without the
 * browser: nothing is pushed, and `onPop` is called by whoever owns the
 * button. That is the packaged app, whose shell asks the page to close its
 * newest thing (`__arkBack`) and leaves only when there is none: the
 * WebView's own history skips entries a page pushed without a fresh tap,
 * so it cannot be trusted to know what is open.
 */
export function createBackStack(history: HistoryLike | null, options: BackStackOptions = {}): BackStack {
  const stack: { readonly id: number; readonly close: Closer }[] = [];
  let seq = 0;
  /** Pops we caused ourselves with `back()`, to be swallowed. */
  let ignore = 0;
  const now = options.now ?? (() => Date.now());
  const leaveWithin = options.leaveWithin ?? LEAVE_WITHIN_MS;
  /** When the warning was last shown; a pop within `leaveWithin` of it leaves. */
  let warnedAt: number | null = null;
  let armed = false;
  const atRoot = (): void => {
    if (options.guard !== true || !armed) return;
    // The pop took the guard entry away; either leave for real, or warn and put it back.
    if (warnedAt != null && now() - warnedAt <= leaveWithin) {
      history?.back();
      return;
    }
    warnedAt = now();
    history?.pushState({ ark: "guard" }, "");
    options.onLeaveWarning?.();
  };
  return {
    push(close) {
      const id = ++seq;
      stack.push({ id, close });
      history?.pushState({ ark: id }, "");
      return () => {
        const index = stack.findIndex((entry) => entry.id === id);
        if (index < 0) return;
        const newest = index === stack.length - 1;
        stack.splice(index, 1);
        if (newest && history != null) {
          ignore += 1;
          history.back();
        }
        // Otherwise its entry stays in the browser's history, under newer ones, and the pop that reaches it closes nothing.
      };
    },
    onPop() {
      if (ignore > 0) {
        ignore -= 1;
        return;
      }
      const top = stack.pop();
      if (top == null) atRoot();
      else top.close();
    },
    armGuard() {
      if (options.guard !== true || armed || history == null) return;
      armed = true;
      history.pushState({ ark: "guard" }, "");
    },
    depth: () => stack.length,
  };
}

/**
 * Whether the page runs as an installed app, with the system's Back and no
 * browser bar. The packaged app is not this: its shell keeps the guard and
 * the "press again" itself (client-app §8), so the page only closes things.
 */
export function isInstalled(): boolean {
  return typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true);
}

/** Who wants to know that Back warned rather than left: the page's toast. */
const warningListeners = new Set<() => void>();

/** What the shell calls on the phone's Back button: true when the page closed something, false when nothing was open and the shell may leave. */
export interface ShellBackWindow {
  __arkBack?: () => boolean;
}

/** The page's one stack, on the real history in a browser, on none in the shell; nothing until the page has a window. */
let shared: BackStack | null = null;
function stackOf(): BackStack {
  if (shared == null) {
    if (shell()) {
      const stack = createBackStack(null, { onLeaveWarning: () => warningListeners.forEach((listener) => listener()) });
      shared = stack;
      (window as ShellBackWindow).__arkBack = () => {
        if (stack.depth() === 0) return false;
        stack.onPop();
        return true;
      };
      return stack;
    }
    shared = createBackStack(window.history, { guard: isInstalled(), onLeaveWarning: () => warningListeners.forEach((listener) => listener()) });
    window.addEventListener("popstate", () => shared?.onPop());
    // The guard goes in on the first touch or key, when the browser will honour it.
    const arm = (): void => shared?.armGuard();
    window.addEventListener("pointerdown", arm, { once: true, capture: true });
    window.addEventListener("keydown", arm, { once: true, capture: true });
  }
  return shared;
}

/** True for a moment after Back warned instead of leaving; the page shows "Press Back again to exit" while it is. */
export function useLeaveWarning(): boolean {
  const [warning, setWarning] = useState(false);
  useEffect(() => {
    stackOf();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const listener = (): void => {
      setWarning(true);
      if (timer != null) clearTimeout(timer);
      timer = setTimeout(() => setWarning(false), LEAVE_WITHIN_MS);
    };
    warningListeners.add(listener);
    return () => {
      warningListeners.delete(listener);
      if (timer != null) clearTimeout(timer);
    };
  }, []);
  return warning;
}

/**
 * While `open`, Back closes this instead of leaving the app. `close` is
 * called when the browser goes back; when the thing is closed by hand
 * instead, its entry goes with it. `enabled` false leaves history alone,
 * for a pane that on a wide screen is furniture rather than a page.
 */
export function useBackClose(open: boolean, close: Closer, enabled = true): void {
  const closer = useRef(close);
  closer.current = close;
  useEffect(() => {
    if (!open || !enabled) return;
    return stackOf().push(() => closer.current());
  }, [open, enabled]);
}

/** Whether the layout is the phone's, or the one where the side pane lies over the chat; kept live as the window changes. */
export function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = (): void => setMatches(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}
